// app/javascript/projects/components/ProjectsDisplay.jsx

import React from 'react';
import { Button } from 'ui'
import {Link} from 'react-router-dom'
import UserDisplaySectionContainer from './UserDisplaySectionContainer';
import { useSubscription } from '../../hooks/subscriptions/queries';
import { isOwner, isSeparatelyInvoiced, isFreeAccount, formatSubscriptionEndDate, formatSubscriptionPrice } from '../../utils/subscriptionUtils';
import { useCurrentUser } from '../../hooks/users/queries';
import { useTeamModal } from '../../hooks/subscriptions/useTeamModal'; 

function getSeparatelyInvoicedCopy(userIsOwner, subscriptionCanceled) {
  if ( !subscriptionCanceled ) {
    return null;
  }
  else if ( !!userIsOwner )  {
    return <div style={{marginBottom: '10px'}}>
      <p>
        Your Delve subscription has expired. Please contact the Delve team to renew it.
      </p>
      <Button variant='blue' id="delve_help_link" href="mailto:team@delvetool.com">
        Contact Us
      </Button>
    </div>
  } else {
    return <p>
      Your Delve subscription has expired. Contact your team owner to renew it, or start your own subscription.
    </p>
  }
}

function subscriptionWillCancelCopy(subscription) {
  return (<p>
      Your plan will end on {formatSubscriptionEndDate(subscription.current_period_end)}.
  </p>)
}

function userIsNotOwnerCopy(subscriptionIsCanceled, subscriptionWillCancel, subscriptionIsPastDue, subscription) {
  if ( subscriptionIsCanceled ) {
    return <p>
      Your subscription has expired. Please contact your team owner about renewing your subscription
      or start a new subscription.
    </p>
  } else if ( subscriptionIsPastDue ) {
    return <p>
      Your subscription is past due. Please ask your team owner to renew the subscription.
    </p>
  }
  else if ( subscriptionWillCancel ) {
    return subscriptionWillCancelCopy(subscription);
  }
  else {
    return (<div>
        <p>Your plan will automatically renew on {formatSubscriptionEndDate(subscription.current_period_end)}.</p>
        <p>Contact your team owner if you have questions about your plan.</p>
    </div>)
  }
}

function userIsOwnerCopy(subscriptionIsCanceled, subscriptionWillCancel, subscriptionIsPastDue, subscription) {
  if ( subscriptionIsCanceled ) {
    return <p>Your subscription has expired. Please renew your subscription to access Delve.</p>
  } else if ( subscriptionIsPastDue ) {
    return <p>Your subscription is past due. Please update your payment information to continue using Delve.</p>
  }
  else if ( subscriptionWillCancel ) {
    return subscriptionWillCancelCopy(subscription);
  } else if ( subscription.status == 'trialing' ) {
    return <p>Your free trial ends on {formatSubscriptionEndDate(subscription.current_period_end)}. After that, you'll be charged {formatSubscriptionPrice(subscription)} per {subscription.plan?.interval || 'month'}.</p>
  } else { // active subscription
    return <p>Your plan will automatically renew on {formatSubscriptionEndDate(subscription.current_period_end)} and you'll be charged {formatSubscriptionPrice(subscription)}.</p>
  }
}

function BillingComponent() {
  const { data: subscription, isLoading } = useSubscription();
  const { data: currentUser } = useCurrentUser();
  const { openBillingModal } = useTeamModal();
  
  const loaded = !isLoading;
  const noSubscription = !subscription && loaded;
  const subscriptionCanceled = subscription?.status === 'canceled';
  const subscriptionIsPastDue = subscription?.status === 'past_due';
  const subscriptionWillCancel = subscription?.cancel_at_period_end;
  const userIsOwner = isOwner(subscription, currentUser?.id);
  const freeAccount = isFreeAccount(subscription);
  const subscriptionIsSeparatelyInvoiced = isSeparatelyInvoiced(subscription);

  if ( freeAccount )
    return (null);

  let subscriptionCopy;
  if ( !loaded )
    subscriptionCopy = null;
  else if ( noSubscription )
    subscriptionCopy = null;
  else if ( subscriptionIsSeparatelyInvoiced ) {
    if ( !subscriptionCanceled ) // if active, don't show anything
      return null;
    subscriptionCopy = getSeparatelyInvoicedCopy(userIsOwner, subscriptionCanceled);
  }
  else if ( !userIsOwner ) {
    subscriptionCopy = userIsNotOwnerCopy(subscriptionCanceled, subscriptionWillCancel, subscriptionIsPastDue, subscription);
  } else {
    subscriptionCopy = userIsOwnerCopy(subscriptionCanceled, subscriptionWillCancel, subscriptionIsPastDue, subscription);
  }

  return (
    <UserDisplaySectionContainer header="Subscription and Billing">
      {subscriptionCopy}

      {userIsOwner && !subscriptionIsSeparatelyInvoiced &&
        <Button variant='blue' onClick={() => openBillingModal()}>
          <span>Manage Subscription and Billing</span>
        </Button>
      }
      {((!userIsOwner && subscriptionCanceled) || (noSubscription)) &&
        <Link style={{ textDecoration: 'none' }} to={`/subscribe`}>
          <Button variant='blue'>Start New Subscription</Button>
        </Link>
      }
    </UserDisplaySectionContainer>
  );
}

export default BillingComponent;
